/**
 * The API client.
 *
 * Every call is a same-origin relative path: in production one Railway service
 * serves the API and this bundle, and in development Vite proxies /api. So
 * there is no base URL to configure and no CORS to get wrong.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    /** Per-field messages from a zod rejection, keyed by field path. */
    readonly fields?: Record<string, string>,
    readonly code?: string,
    /** The server's log reference for a 500, worth quoting in a bug report. */
    readonly reference?: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  body?: unknown;
  signal?: AbortSignal;
}

async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = 'GET', body, signal } = options;

  const headers: Record<string, string> = {};
  if (body !== undefined) headers['content-type'] = 'application/json';

  const response = await fetch(path, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
    // Both sides authenticate with an httpOnly cookie the server set — the
    // admin session and the payer session. There is no bearer token to
    // attach, and nothing readable from JavaScript, which is the point.
    credentials: 'same-origin',
    signal,
  });

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  let payload: unknown = null;
  if (text) {
    try {
      payload = JSON.parse(text);
    } catch {
      // A non-JSON body from an API route means something upstream answered —
      // a proxy error page, most likely. Do not show its HTML to the user.
      throw new ApiError(response.status, 'The server sent back something unexpected.');
    }
  }

  if (!response.ok) {
    const error = (payload ?? {}) as {
      error?: string;
      fields?: Record<string, string>;
      code?: string;
      reference?: string;
    };
    throw new ApiError(
      response.status,
      error.error ?? `Request failed (${response.status}).`,
      error.fields,
      error.code,
      error.reference,
    );
  }

  return payload as T;
}

export const api = {
  get: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'GET' }),
  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'PATCH', body }),
  delete: <T>(path: string, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    request<T>(path, { ...options, method: 'DELETE' }),
};

/**
 * The customer flow. Identical mechanics now that both sides use cookies —
 * kept as a separate export so a page in pay/ cannot reach an admin helper by
 * autocomplete.
 */
export const publicApi = {
  get: <T>(path: string) => request<T>(path, { method: 'GET' }),
  post: <T>(path: string, body?: unknown) => request<T>(path, { method: 'POST', body }),
};
